import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/api";
import { useLanguage } from "@/context/LanguageContext";

const inputCls =
  "w-full bg-obsidian border border-white/15 px-5 py-4 text-white placeholder:text-white/30 focus:border-volt focus:outline-none transition-colors duration-300";
const labelCls = "block font-jetbrains text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (token) {
        await api.post("/auth/reset-password", { token, password });
        toast.success(t("reset.okNew"));
      } else {
        await api.post("/auth/forgot-password", { email, lang: localStorage.getItem("tsc_lang") === "fr" ? "fr" : "en" });
        toast.success(t("reset.okSent"));
      }
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-[#F5F5F5] font-satoshi flex px-6 py-10" data-testid="reset-page">
      <div className="max-w-md w-full mx-auto my-auto">
        <Link to="/login" className="inline-flex items-center gap-2 text-white/50 hover:text-volt transition-colors text-sm uppercase tracking-widest mb-10" data-testid="reset-back-link">
          <ArrowLeft size={16} /> {t("reset.back")}
        </Link>

        <h1 className="font-cabinet font-black uppercase text-4xl md:text-5xl tracking-tight mb-10">
          {token ? t("reset.h1new") : t("reset.h1reset")}
        </h1>

        {done ? (
          <div className="bg-surface border border-white/10 p-8" data-testid="reset-done">
            <p className="text-white/70 leading-relaxed">
              {token ? t("reset.doneNew") : t("reset.doneSent")}
            </p>
            <Link to="/login" data-testid="reset-login-link" className="inline-block mt-6 text-volt hover:text-white transition-colors font-cabinet font-bold uppercase tracking-wide">
              {t("reset.goLogin")}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            {token ? (
              <div>
                <label htmlFor="reset-password" className={labelCls}>{t("reset.newPw")}</label>
                <input id="reset-password" data-testid="reset-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
              </div>
            ) : (
              <div>
                <label htmlFor="reset-email" className={labelCls}>{t("reset.email")}</label>
                <input id="reset-email" data-testid="reset-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className={inputCls} />
              </div>
            )}
            {error && <p className="text-crimson text-sm" data-testid="reset-error">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit-button"
              className="w-full rounded-full bg-volt text-obsidian font-cabinet font-bold uppercase tracking-wide px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-50"
            >
              {busy ? t("reset.busy") : token ? t("reset.update") : t("reset.sendLink")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
